import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { emboldenSearchTerm, formatPrice } from 'utils';
import ProductImage from 'components/Image/ProductImage';
import Image from 'components/Image/Image';
import FadeCollapse from 'components/FadeCollapse/FadeCollapse';
import InfiniteScroll from 'react-infinite-scroller';
import styles from './ItemInlineSearch.module.scss';

export interface ItemType {
  item: { name: string; imageUri: string; price: string; description: string; key: string };
  enabled: boolean;
}
interface Props {
  values: ItemType[];
  loading: boolean;
  handleChange: (value: string) => void;
  handleSubmit: (index: number) => void;
  handleLoadMore: (searchTerm: string, offset: number) => Promise<void>;
  setHasMore: (hasMore: boolean) => void;
  hasMore: boolean;
  buttonText: string;
  initText: string;
}

const ItemInlineSearch: React.FC<Props> = ({
  values,
  handleChange,
  handleSubmit,
  handleLoadMore,
  loading,
  hasMore,
  setHasMore,
  buttonText,
  initText
}) => {
  const inputRef = useRef<HTMLInputElement | null>();
  const outerRef = useRef<HTMLDivElement | null>();

  const [textValue, setTextValue] = useState('');
  const [hoveredRowIndex, setHoveredRowIndex] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [pageOffset, setOffset] = useState(1);
  const [loadingAdditional, setLoadingAdditional] = useState(false);

  const [matches, setMatches] = useState(window.matchMedia('(min-width: 768px)').matches);
  const trimmedTextValue = useMemo((): string => {
    return textValue.trim().toLowerCase();
  }, [textValue]);

  const toggleOption = useCallback(
    (itemIndex: number, clearInput?: boolean) => {
      setIsInputFocused(false);

      if (clearInput) {
        setTextValue('');
        setHoveredRowIndex(0);
      }
      handleSubmit(itemIndex);
    },
    [handleSubmit]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (outerRef.current && !outerRef.current.contains(e.target as Node)) {
        setIsInputFocused(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    window.matchMedia('(min-width: 768px)').addEventListener('change', (e) => setMatches(e.matches));
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    handleChange(trimmedTextValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTextValue]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Escape') {
        setIsInputFocused(false);
        return;
      }
      if (values.length) {
        let newHoveredRow = hoveredRowIndex;
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            if (newHoveredRow === values.length - 1) {
              newHoveredRow = 0;
            } else {
              newHoveredRow += 1;
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            if (!newHoveredRow) {
              newHoveredRow = values.length - 1;
            } else {
              newHoveredRow -= 1;
            }
            break;
          case 'Enter':
            if (values[hoveredRowIndex].enabled) toggleOption(hoveredRowIndex, true);
            break;
          default:
            break;
        }

        if (newHoveredRow !== hoveredRowIndex) {
          setHoveredRowIndex(newHoveredRow);
        }
      }
    },
    [hoveredRowIndex, toggleOption, values]
  );

  const handleInfiniteScroll = useCallback(async () => {
    if (!hasMore || loadingAdditional || loading || trimmedTextValue.length < 2) return;
    setLoadingAdditional(true);
    await handleLoadMore(trimmedTextValue, pageOffset);
    setOffset((previous) => previous + 1);
    setLoadingAdditional(false);
  }, [hasMore, loadingAdditional, loading, trimmedTextValue, handleLoadMore, pageOffset]);

  useEffect(() => {
    setOffset(1);
  }, [textValue]);

  const loadingListItem = useMemo(() => {
    return (
      <li key="results-loading-element" className={`${styles.Result} ${styles.Loading}`}>
        <Image alt="loading-image" src="/Images/NoImage.svg" className={styles.ImageIsLoading} isLoading />
        <div>
          <span className="LoadingCoverText">Loading...</span>
          <p className="LoadingCoverText">{formatPrice(0)}</p>
          <p className="LoadingCoverText">XXXXXXXXXXXX</p>
        </div>
      </li>
    );
  }, []);

  return (
    <div ref={(ref) => (outerRef.current = ref)}>
      <div className={`${styles.Top} ${isInputFocused ? styles.Input : ''}`} data-testid="product-search-top">
        {isInputFocused ? (
          <>
            <input
              data-testid="search-input"
              value={textValue}
              type="text"
              onFocus={() => {
                setTextValue('');
              }}
              onBlur={() => {
                setHasMore(false);
              }}
              onChange={(e) => {
                setHasMore(false);
                setTextValue(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              ref={(ref) => (inputRef.current = ref)}
              placeholder={values.length ? '' : initText}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className="amp-unmask"
            />
            <button
              type="button"
              aria-label="clear search input"
              onClick={() => {
                setIsInputFocused(false);
                setTextValue('');
              }}
            >
              <i className={`mm-${loading || loadingAdditional ? `circle-o-notch mm-spin` : `times`}`} />
            </button>
          </>
        ) : (
          <button
            data-testid="search-focus-button"
            type="button"
            className={styles.FocusButton}
            onClick={() => setIsInputFocused(true)}
          >
            <h3>{buttonText}</h3>
          </button>
        )}
      </div>
      <FadeCollapse open={isInputFocused}>
        {trimmedTextValue.length >= 2 && isInputFocused ? (
          <div className={styles.SearchResultsContainer}>
            <InfiniteScroll
              loadMore={async () => await handleInfiniteScroll()}
              hasMore={hasMore}
              loader={loadingListItem}
              useWindow={false}
              threshold={100}
              element="ul"
              data-testid="inline-search-list"
              className={styles.Dropdown}
            >
              <>
                {!values.length && !loading && (
                  <li>
                    <div className={styles.NoResult}>No results found.</div>
                  </li>
                )}
                {values.map(({ item, enabled }, index) => {
                  if (enabled) {
                    const isHovered = hoveredRowIndex === index;

                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          className={`${styles.Result} ${isHovered ? styles.Hovered : ''}`}
                          onMouseEnter={() => setHoveredRowIndex(index)}
                          onClick={() => toggleOption(index, true)}
                        >
                          <ProductImage alt="" src={item.imageUri || '/Images/NoImage.svg'} />
                          <div className={styles.TextElements}>
                            <span className={styles.ResultName}>{emboldenSearchTerm(item.name, trimmedTextValue)}</span>
                            <p>{item.price}</p>
                            <p>{item.description}</p>
                          </div>
                        </button>
                      </li>
                    );
                  }

                  return (
                    <li key={item.key}>
                      <div className={`${styles.Result} ${styles.Disabled}`}>
                        <ProductImage alt="" src={item.imageUri || '/Images/NoImage.svg'} />
                        <div className={styles.TextElements}>
                          <span className={styles.ResultName}>{emboldenSearchTerm(item.name, trimmedTextValue)}</span>
                          <span className={styles.AlreadyAdded}>{matches ? 'ITEM ADDED' : 'ADDED'}</span>
                          <p>{item.price}</p>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
                {loading ? loadingListItem : null}
              </>
            </InfiniteScroll>
          </div>
        ) : null}
      </FadeCollapse>
    </div>
  );
};

export default ItemInlineSearch;
